import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react";
import NotificationHeaderLightIcon from "../../Assets/Images/notification-icon.svg";
import NotificationHeaderDarkIcon from "../../Assets/Images/notification-dark-icon.png";
import MarkAllReadIcon from "../../Assets/Images/mark-all-read-icon.svg";
import Button from "../../Common/Button/Button";
import { DynamicContext } from "../../Context/DynamicContext";
import {
  VYLABS_BASE_URL,
  NOTIFICATIONS_GET,
  NOTIFICATIONS_READ_ALL,
  NOTIFICATIONS_READ_BY_ID,
  WEB_SOCKET_URL,
} from "../../helper/url_helper";
import { post, get } from "../../helper/Api_helper";
import { Toast } from "../../Common/Toast-Snackbar/ToastSnackbar";
import NotificationCard from "./NotificationCard";
import AnimationPlayer from "../../Common/Animation-Player/AnimationPlayer";
import VybogTALLoaderAnimation from "../../Common/Dynamic-Animations/DynamicAnimations";
import NodatafoundAnimation from "../../Assets/Json/nodata-found-robot-animation.json";
import backArrowIcon from "../../Assets/Images/arrow-back-icon.png";
import { useNavigate } from "react-router-dom";
import "./Notifications.css";

const NOTIFICATION_PING_INTERVAL = 30000;
const NOTIFICATION_PING_TIMEOUT = 10000;
const MAX_NOTIFICATION_RECONNECT_ATTEMPTS = 5;
const BASE_NOTIFICATION_RECONNECT_DELAY = 1000;

function Notifications() {
  const navigate = useNavigate();
  const { isDarkMode, userDetails, setNotificationsState } =
    useContext(DynamicContext);
  const [storeNotifications, setStoreNotifications] = useState([]);
  const [filterType, setFilterType] = useState("all"); // all, unread, application_reminder, client_reminder
  const [loading, setLoading] = useState(false);
  const notificationSocketRef = useRef(null);
  const notificationPingIntervalRef = useRef(null);
  const notificationPingTimeoutRef = useRef(null);
  const notificationConnectTimeoutRef = useRef(null);
  const notificationReconnectTimeoutRef = useRef(null);
  const notificationReconnectAttemptsRef = useRef(0);
  const notificationConnectingRef = useRef(false);
  const notificationUnmountedRef = useRef(false);
  const notificationIntentionalCloseRef = useRef(false);
  const fetchAllNotificationsRef = useRef(null);
  const filterTypeRef = useRef(filterType);

  useEffect(() => {
    filterTypeRef.current = filterType;
  }, [filterType]);

  const mergeNotificationPayload = useCallback((prev, notificationPayload) => {
    const previousNotifications = Array.isArray(prev?.notifications)
      ? prev.notifications
      : [];
    const alreadyExists = previousNotifications.some(
      (item) => item.id === notificationPayload.id,
    );
    const notifications = alreadyExists
      ? previousNotifications.map((item) =>
          item.id === notificationPayload.id
            ? { ...item, ...notificationPayload }
            : item,
        )
      : [notificationPayload, ...previousNotifications];

    return {
      ...prev,
      notifications,
      unread_count:
        typeof notificationPayload.unread_count === "number"
          ? notificationPayload.unread_count
          : (prev?.unread_count || 0) + (alreadyExists ? 0 : 1),
    };
  }, []);

  const updateNotificationStateFromPayload = useCallback(
    (notificationPayload) => {
      if (!notificationPayload?.id) return;

      setStoreNotifications((prev) => {
        const mergedState = mergeNotificationPayload(prev, notificationPayload);
        
        // Check if notification should be shown based on current filter
        const currentFilter = filterTypeRef.current;
        let shouldShowNotification = true;
        
        if (currentFilter === "unread" && notificationPayload.read_at) {
          shouldShowNotification = false;
        } else if (currentFilter === "application_reminder" && notificationPayload.type !== "application_callback_reminder") {
          shouldShowNotification = false;
        } else if (currentFilter === "client_reminder" && notificationPayload.type !== "client_callback_reminder") {
          shouldShowNotification = false;
        }

        if (shouldShowNotification) return mergedState;

        return {
          ...mergedState,
          notifications: mergedState.notifications.filter(
            (notification) => notification.id !== notificationPayload.id,
          ),
        };
      });

      if (setNotificationsState) {
        setNotificationsState((prev) =>
          mergeNotificationPayload(prev, notificationPayload),
        );
      }
    },
    [mergeNotificationPayload, setNotificationsState],
  );

  const closeNotificationWebSocket = useCallback(() => {
    notificationIntentionalCloseRef.current = true;
    if (notificationSocketRef.current) {
      notificationSocketRef.current.close(1000, "Notifications page cleanup");
      notificationSocketRef.current = null;
    }
    if (notificationPingIntervalRef.current) {
      clearInterval(notificationPingIntervalRef.current);
      notificationPingIntervalRef.current = null;
    }
    if (notificationPingTimeoutRef.current) {
      clearTimeout(notificationPingTimeoutRef.current);
      notificationPingTimeoutRef.current = null;
    }
    if (notificationConnectTimeoutRef.current) {
      clearTimeout(notificationConnectTimeoutRef.current);
      notificationConnectTimeoutRef.current = null;
    }
    if (notificationReconnectTimeoutRef.current) {
      clearTimeout(notificationReconnectTimeoutRef.current);
      notificationReconnectTimeoutRef.current = null;
    }
    notificationConnectingRef.current = false;
  }, []);

  const startNotificationPingInterval = useCallback((ws) => {
    if (notificationPingIntervalRef.current) {
      clearInterval(notificationPingIntervalRef.current);
    }

    const sendPing = () => {
      if (ws.readyState !== WebSocket.OPEN) return;
      console.warn("Sending ping");
      ws.send("ping");
      if (notificationPingTimeoutRef.current) {
        clearTimeout(notificationPingTimeoutRef.current);
      }
      notificationPingTimeoutRef.current = setTimeout(() => {
        notificationIntentionalCloseRef.current = false;
        ws.close(4000, "Notification ping timeout");
      }, NOTIFICATION_PING_TIMEOUT);
    };

    sendPing();
    notificationPingIntervalRef.current = setInterval(
      sendPing,
      NOTIFICATION_PING_INTERVAL,
    );
  }, []);

  const initializeNotificationWebSocket = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token || !userDetails?.id || notificationConnectingRef.current) {
      return;
    }
    if (
      notificationSocketRef.current &&
      (notificationSocketRef.current.readyState === WebSocket.OPEN ||
        notificationSocketRef.current.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    notificationConnectingRef.current = true;
    if (notificationSocketRef.current) {
      notificationIntentionalCloseRef.current = true;
      notificationSocketRef.current.close();
    }
    if (notificationPingIntervalRef.current) {
      clearInterval(notificationPingIntervalRef.current);
      notificationPingIntervalRef.current = null;
    }
    if (notificationPingTimeoutRef.current) {
      clearTimeout(notificationPingTimeoutRef.current);
      notificationPingTimeoutRef.current = null;
    }
    if (notificationConnectTimeoutRef.current) {
      clearTimeout(notificationConnectTimeoutRef.current);
      notificationConnectTimeoutRef.current = null;
    }
    if (notificationReconnectTimeoutRef.current) {
      clearTimeout(notificationReconnectTimeoutRef.current);
      notificationReconnectTimeoutRef.current = null;
    }

    const baseHost = String(WEB_SOCKET_URL)
      .replace(/^wss?:\/\//, "")
      .replace(/^https?:\/\//, "")
      .replace(/^\/+/, "");
    const wsUrl = `wss://${baseHost}/ws?token=${encodeURIComponent(
      "Bearer " + token,
    )}&channel_id=${encodeURIComponent(userDetails.id)}`;

    try {
      notificationIntentionalCloseRef.current = false;
      console.log("Creating new WebSocket connection...");
      const ws = new WebSocket(wsUrl);
      notificationSocketRef.current = ws;

      ws.onopen = () => {
        console.log("WebSocket connection established");
        notificationReconnectAttemptsRef.current = 0;
        notificationConnectingRef.current = false;
        startNotificationPingInterval(ws);
      };

      ws.onmessage = (event) => {
        if (event.data === "pong") {
          console.warn("Pong received");
          if (notificationPingTimeoutRef.current) {
            clearTimeout(notificationPingTimeoutRef.current);
            notificationPingTimeoutRef.current = null;
          }
          return;
        }

        try {
          console.log("Raw WebSocket message:", event.data);
          const cleanedMessage = event.data.trim();
          const messageParts = cleanedMessage.split(/\|(.+)/, 2);
          if (messageParts.length !== 2) return;

          const topic = messageParts[0];
          const baseTopic = topic.split(":")[0];
          if (baseTopic !== "notification") {
            console.log("Ignored WebSocket topic:", baseTopic);
            return;
          }

          const notificationPayload = JSON.parse(messageParts[1]);
          console.log("Received notification:", notificationPayload);
          updateNotificationStateFromPayload(notificationPayload);
          fetchAllNotificationsRef.current?.({ silent: true });
        } catch (error) {
          console.error(
            "Error processing notification WebSocket message:",
            error,
          );
        }
      };

      ws.onclose = (event) => {
        console.log("WebSocket connection closed", event);
        notificationConnectingRef.current = false;
        if (notificationPingIntervalRef.current) {
          clearInterval(notificationPingIntervalRef.current);
          notificationPingIntervalRef.current = null;
        }
        if (notificationPingTimeoutRef.current) {
          clearTimeout(notificationPingTimeoutRef.current);
          notificationPingTimeoutRef.current = null;
        }

        if (
          !notificationUnmountedRef.current &&
          !notificationIntentionalCloseRef.current &&
          notificationReconnectAttemptsRef.current <
            MAX_NOTIFICATION_RECONNECT_ATTEMPTS
        ) {
          const delay = Math.min(
            BASE_NOTIFICATION_RECONNECT_DELAY *
              Math.pow(2, notificationReconnectAttemptsRef.current),
            30000,
          );
          notificationReconnectTimeoutRef.current = setTimeout(() => {
            notificationReconnectAttemptsRef.current += 1;
            initializeNotificationWebSocket();
          }, delay);
        }
      };

      ws.onerror = (error) => {
        console.warn("Notification WebSocket error:", error);
        notificationConnectingRef.current = false;
      };
    } catch (error) {
      console.error("Error creating notification WebSocket:", error);
      notificationConnectingRef.current = false;
    }
  }, [
    startNotificationPingInterval,
    updateNotificationStateFromPayload,
    userDetails?.id,
  ]);

  // Function to get filtered notifications based on filter type
  const getFilteredNotifications = useCallback(() => {
    if (!storeNotifications?.notifications) return [];
    
    let filtered = storeNotifications.notifications;
    
    if (filterType === "unread") {
      filtered = filtered.filter(notification => !notification.read_at);
    } else if (filterType === "application_reminder") {
      filtered = filtered.filter(
        notification => notification.type === "application_callback_reminder"
      );
    } else if (filterType === "client_reminder") {
      filtered = filtered.filter(
        notification => notification.type === "client_callback_reminder"
      );
    }
    
    return filtered;
  }, [storeNotifications, filterType]);

  const fetchAllNotifications = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) {
        setLoading(true);
      }
      try {
        // Build the endpoint based on filter
        let endpoint = NOTIFICATIONS_GET;
        const params = [];
        
        if (filterType === "unread") {
          params.push("unread_only=true");
        }
        
        // Note: API might not support filtering by type on the backend
        // If it does, you can add it here
        // if (filterType === "application_reminder") {
        //   params.push("type=application_callback_reminder");
        // } else if (filterType === "client_reminder") {
        //   params.push("type=client_callback_reminder");
        // }
        
        if (params.length > 0) {
          endpoint += `?${params.join("&")}`;
        }

        const res = await get(VYLABS_BASE_URL, endpoint);
        if (res) {
          setStoreNotifications(res);
          if (setNotificationsState) {
            setNotificationsState({
              notifications: res?.notifications || [],
              unread_count: res?.unread_count || 0,
            });
          }
        }
      } catch (error) {
        console.error("error in fetchAllNotifications", error);
        Toast("error", "Failed to fetch notifications");
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [filterType, setNotificationsState],
  );

  useEffect(() => {
    fetchAllNotificationsRef.current = fetchAllNotifications;
  }, [fetchAllNotifications]);

  useEffect(() => {
    fetchAllNotifications();
  }, [fetchAllNotifications]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !userDetails?.id) return undefined;

    notificationUnmountedRef.current = false;
    notificationConnectTimeoutRef.current = setTimeout(() => {
      initializeNotificationWebSocket();
    }, 250);

    return () => {
      notificationUnmountedRef.current = true;
      closeNotificationWebSocket();
    };
  }, [
    closeNotificationWebSocket,
    initializeNotificationWebSocket,
    userDetails?.id,
  ]);

  const handleMarkAllRead = async () => {
    try {
      await post(VYLABS_BASE_URL, NOTIFICATIONS_READ_ALL);
      Toast("success", "Marked all as read successfully!");
      await fetchAllNotifications();
    } catch (error) {
      console.error("error in handleMarkAllRead", error);
      Toast("error", "Failed to mark all as read");
    }
  };

  const handleMarkReadById = async (id) => {
    try {
      await post(VYLABS_BASE_URL, `${NOTIFICATIONS_READ_BY_ID}/${id}`);
      Toast("success", "Marked notification as read successfully");
      await fetchAllNotifications();
    } catch (error) {
      console.error("error in handleMarkReadById", error);
      Toast("error", "Failed to mark notification as read");
    }
  };

  // Get the filtered notifications for display
  const filteredNotifications = getFilteredNotifications();

  return (
    <div className="conversations-page notifications-page">
      <div
        className="header-main-container"
        style={{ justifyContent: "space-between" }}
      >
        <div className="header-icon-title-container">
          <img
            src={
              isDarkMode
                ? NotificationHeaderDarkIcon
                : NotificationHeaderLightIcon
            }
            alt="Notifications"
            className="header-title-icon"
          />
          <h2 style={{ margin: 0 }}>Notifications</h2>
          {storeNotifications?.unread_count > 0 && (
            <span
              style={{
                background: "#6963EC",
                color: "#FFFFFF",
                borderRadius: "50%",
                padding: "2px 8px",
                fontSize: "12px",
                fontWeight: 600,
                marginLeft: "8px",
              }}
            >
              {storeNotifications.unread_count}
            </span>
          )}
        </div>
        <div className="notfication-button-container">
          <Button
            label="Back To Dashboard"
            event={() => navigate("/dashboard")}
            iconSrc={backArrowIcon}
          />
        </div>
      </div>

      <div
        className="notifications-content"
        style={{
          padding: "25px",
          display: "flex",
          flexDirection: "column",
          gap: "15px",
        }}
      >
        <div
          className="notifications-toolbar"
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "10px",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <div
            className="notifications-filter-group"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "13px",
              flexWrap: "wrap",
            }}
          >
            <Button
              label="All"
              event={() => setFilterType("all")}
              backgroundColor={
                filterType === "all"
                  ? "var(--primary__color)"
                  : "var(--card__background)"
              }
              color={
                filterType === "all" ? "#FFFFFF" : "var(--job-Description_Color)"
              }
              border={
                filterType === "all"
                  ? "1px solid var(--primary__color)"
                  : "1px solid var(--border__color)"
              }
              style={{
                borderRadius: "20px",
                padding: "4px 16px",
                fontSize: "13px",
              }}
              hoverd={false}
            />
            <Button
              label="Unread"
              event={() => setFilterType("unread")}
              backgroundColor={
                filterType === "unread"
                  ? "var(--primary__color)"
                  : "var(--card__background)"
              }
              color={
                filterType === "unread" ? "#FFFFFF" : "var(--job-Description_Color)"
              }
              border={
                filterType === "unread"
                  ? "1px solid var(--primary__color)"
                  : "1px solid var(--border__color)"
              }
              style={{
                borderRadius: "20px",
                padding: "4px 16px",
                fontSize: "13px",
              }}
              hoverd={false}
            />
            <Button
              label="Candidate Reminder"
              event={() => setFilterType("application_reminder")}
              backgroundColor={
                filterType === "application_reminder"
                  ? "var(--primary__color)"
                  : "var(--card__background)"
              }
              color={
                filterType === "application_reminder" ? "#FFFFFF" : "var(--job-Description_Color)"
              }
              border={
                filterType === "application_reminder"
                  ? "1px solid var(--primary__color)"
                  : "1px solid var(--border__color)"
              }
              style={{
                borderRadius: "20px",
                padding: "4px 16px",
                fontSize: "13px",
              }}
              hoverd={false}
            />
            <Button
              label="Client Reminder"
              event={() => setFilterType("client_reminder")}
              backgroundColor={
                filterType === "client_reminder"
                  ? "var(--primary__color)"
                  : "var(--card__background)"
              }
              color={
                filterType === "client_reminder" ? "#FFFFFF" : "var(--job-Description_Color)"
              }
              border={
                filterType === "client_reminder"
                  ? "1px solid var(--primary__color)"
                  : "1px solid var(--border__color)"
              }
              style={{
                borderRadius: "20px",
                padding: "4px 16px",
                fontSize: "13px",
              }}
              hoverd={false}
            />
          </div>
          <div className="notifications-mark-action">
            <Button
              label="Mark all read"
              event={handleMarkAllRead}
              backgroundColor="var(--card__background)"
              color="var(--job-Description_Color)"
              border="1px solid var(--border__color)"
              style={{ borderRadius: "16px", padding: "5px 12px" }}
              hoverd={false}
            >
              <img
                src={MarkAllReadIcon}
                alt="Mark all read"
                style={{
                  width: "16px",
                  height: "16px",
                  filter: isDarkMode ? "brightness(0) invert(1)" : "none",
                }}
              />
            </Button>
          </div>
        </div>

        {loading ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              marginTop: "150px",
            }}
          >
            <VybogTALLoaderAnimation />
          </div>
        ) : !filteredNotifications.length ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              padding: "20px",
            }}
          >
            <div
              style={{
                textAlign: "center",
                color: "var(--job-Description_Color)",
                fontSize: "14px",
                marginTop: "10px",
              }}
            >
              {filterType === "unread" && "No unread notifications"}
              {filterType === "application_reminder" && "No application reminder notifications"}
              {filterType === "client_reminder" && "No client reminder notifications"}
              {filterType === "all" && "No notifications yet"}
            </div>
            <AnimationPlayer
              animationSrc={NodatafoundAnimation}
              playerHeight="200px"
              playerWidth="300px"
            />
          </div>
        ) : (
          filteredNotifications.map((notification) => (
            <NotificationCard
              key={notification.id}
              id={notification.id}
              title={notification.title}
              body={notification.body}
              type={notification.type}
              read_at={notification.read_at}
              created_at={notification.created_at}
              onMarkAsRead={handleMarkReadById}
              isDarkMode={isDarkMode}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default Notifications;